/**
 * The science site's Overview and Tables tabs — its figures and its ranked
 * lists (ScienceSiteReport.tsx holds the filter bar and the tab strip).
 */
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { InfoTip } from '@shared-ui/components';
import { simulationsApi } from '../../../api/simulationsApi';
import type { SiteAnalytics as Data } from '../../../api/siteAnalyticsApi';
import { referrerKindLabel } from '../shared/sourceLabels';
import { Table } from '../shared/RankedTable';
import { facetOptions } from '../shared/rankedRows';
import { Trend } from '../shared/Trend';
import { boardGradeLabel, cap, pageRows, referrerFacet, simulationRows } from './scienceRows';

export function ScienceOverview({ data }: { data: Data }) {
  return (
    <>
    <div className="sa-stats">
      {([
        // Who, then how often they came, then what they did.
        ['Users', data.totals.users, 'distinct readers',
          'Different readers in this window. The site makes a guest account on a reader’s first page view, so this is roughly distinct people — strictly, distinct browsers: one person on two browsers who never signs in counts twice.'],
        ['Sessions', data.totals.visits, 'browser-tab sessions',
          'A session ends when its browser tab closes, so a reader who comes back later is a second session. Counted once over the whole window, even when it runs past midnight.'],
        ['Page views', data.totals.pageViews, 'pages read',
          'Every page loaded — the home page, a chapter, a simulation’s page. Two pages in one session are two page views.'],
        ['Simulations opened', data.totals.simulationOpens, 'times',
          'Each time a simulation was opened. The same simulation opened twice counts 2.'],
        ['Chemistry lab used', data.totals.labUses, 'sessions',
          'Sessions in which someone mixed at least once on a chemistry bench. Recorded by the server as it computes the mix, so opening the bench page alone does not count.'],
      ] as const).filter(([, value]) => value !== undefined).map(([label, value, note, definition]) => (
        <div className="sa-stat" key={label}>
          <span className="sa-stat-value">{(value as number).toLocaleString()}</span>
          <span className="sa-stat-label">{label}<InfoTip text={definition} /></span>
          <span className="sa-stat-note">{note}</span>
        </div>
      ))}
    </div>

    <section className="sa-panel">
      <h2>Per {data.trendUnit}</h2>
      <Trend points={data.trend} unit={data.trendUnit} />
    </section>
    </>
  );
}

export function ScienceTables({ data }: { data: Data }) {
  // Titles, subjects and audiences for the simulations table — its traffic
  // cannot say what a simulation is about.
  const { data: catalogList } = useQuery({ queryKey: ['simulations'], queryFn: simulationsApi.list, staleTime: 5 * 60_000 });
  const catalog = useMemo(() => new Map((catalogList ?? []).map(s => [s.id, s])), [catalogList]);
  return (
    <>
    <div className="sa-grid">
      <Table
        title="Most-visited pages"
        labelHead="Page"
        rows={pageRows(data.topPages)}
        filters={[{
          key: 'section', kind: 'select', placeholder: 'All sections',
          options: facetOptions(pageRows(data.topPages), 'section'),
        }]}
      />
      <Table
        title="Most-read simulations"
        labelHead="Simulation"
        rows={simulationRows(data.topSimulations, catalog)}
        filters={catalog.size === 0 ? [] : [
          { key: 'subject', kind: 'select', placeholder: 'All subjects',
            options: facetOptions(simulationRows(data.topSimulations, catalog), 'subject', cap) },
          { key: 'boardGrade', kind: 'select', placeholder: 'All boards & grades',
            options: facetOptions(simulationRows(data.topSimulations, catalog), 'boardGrade', boardGradeLabel) },
          { key: 'status', kind: 'select', placeholder: 'Live & draft',
            options: facetOptions(simulationRows(data.topSimulations, catalog), 'status', cap) },
        ]}
      />
    </div>

    <Table
      title="Where they came from"
      labelHead="Referrer"
      numbers={['visits']}
      sortBy="visits"
      rows={data.referrers.map(r => ({
        key: r.referrerHost, label: r.referrerHost,
        ...(r.kind && !r.kind.startsWith('other:') ? { sub: referrerKindLabel(r.kind) } : {}),
        visits: r.visits,
        search: r.referrerHost.toLowerCase(),
        facets: { kind: referrerFacet(r.kind) },
      }))}
      filters={[{
        key: 'kind', kind: 'select', placeholder: 'All kinds',
        options: facetOptions(
          data.referrers.map(r => ({ facets: { kind: referrerFacet(r.kind) } })),
          'kind', k => (k === 'other' ? 'Other' : referrerKindLabel(k)),
        ),
      }]}
    />
    </>
  );
}
